/**
 * NLP Extension for Scratch (PictoBlox-compatible)
 * ================================================
 * Implements PictoBlox-compatible "naturalLanguageProcessing" blocks.
 * Currently provides stub implementations for loading .sb3 files
 * like Virtual Doctor.
 */
import formatMessage from 'format-message';
import nlp from 'compromise';
import Sentiment from 'sentiment';

class NaiveBayes {
    constructor () {
        this.vocab = new Set();
        this.classCounts = {};
        this.classWordCounts = {};
        this.totalDocs = 0;
    }
    
    tokenize (text) {
        return String(text).toLowerCase()
            .match(/\w+/g) || [];
    }
    
    learn (text, className) {
        const tokens = this.tokenize(text);
        if (!this.classCounts[className]) {
            this.classCounts[className] = 0;
            this.classWordCounts[className] = {};
        }
        this.classCounts[className]++;
        this.totalDocs++;
        tokens.forEach(token => {
            this.vocab.add(token);
            this.classWordCounts[className][token] = (this.classWordCounts[className][token] || 0) + 1;
        });
    }

    _getProbabilities (text) {
        const tokens = this.tokenize(text);
        const probs = {};
        let totalProb = 0;
        
        // Laplace smoothing
        const vocabSize = this.vocab.size || 1;
        
        for (const className in this.classCounts) {
            let logProb = Math.log(this.classCounts[className] / this.totalDocs);
            const totalWordsInClass = Object.values(this.classWordCounts[className])
                .reduce((a, b) => a + b, 0);
            
            tokens.forEach(token => {
                const count = this.classWordCounts[className][token] || 0;
                logProb += Math.log((count + 1) / (totalWordsInClass + vocabSize));
            });
            
            const p = Math.exp(logProb);
            probs[className] = p;
            totalProb += p;
        }

        // Normalize
        for (const className in probs) {
            probs[className] = totalProb > 0 ? (probs[className] / totalProb) : 0;
        }
        
        return probs;
    }

    categorize (text) {
        const probs = this._getProbabilities(text);
        let bestClass = 'unknown';
        let maxProb = -1;
        for (const className in probs) {
            if (probs[className] > maxProb) {
                maxProb = probs[className];
                bestClass = className;
            }
        }
        return bestClass;
    }

    getConfidence (text, className) {
        const probs = this._getProbabilities(text);
        return probs[className] ? (probs[className] * 100).toFixed(1) : 0;
    }
}

class NLPExtension {
    constructor (runtime) {
        this.runtime = runtime;
        this._classifier = new NaiveBayes();
        this._isTrained = false;
        this._sentimentAnalyzer = new Sentiment();
    }

    getInfo () {
        return {
            id: 'naturalLanguageProcessing',
            name: formatMessage({
                id: 'gui.extension.nlp.name',
                default: 'Natural Language Processing'
            }),
            color1: '#FF6680',
            color2: '#FF4D6A',
            blocks: [
                {
                    opcode: 'addDocument',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'nlp.blocks.addText',
                        default: 'add text [TEXT] to class [LABEL]'
                    }),
                    arguments: {
                        TEXT: {
                            type: 'string',
                            defaultValue: 'Hello'
                        },
                        LABEL: {
                            type: 'string',
                            defaultValue: 'greeting'
                        }
                    }
                },
                {
                    opcode: 'train',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'nlp.blocks.trainClassifier',
                        default: 'train text classifier'
                    })
                },
                {
                    opcode: 'resetClassifier',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'nlp.blocks.resetClassifier',
                        default: 'reset text classifier'
                    })
                },
                {
                    opcode: 'analyseText',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'nlp.blocks.getClass',
                        default: 'get class of [TEXT]'
                    }),
                    arguments: {
                        TEXT: {
                            type: 'string',
                            defaultValue: 'Hello'
                        }
                    }
                },
                {
                    opcode: 'getConfidence',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'nlp.blocks.getConfidence',
                        default: 'get confidence of [CLASS] for [TEXT]'
                    }),
                    arguments: {
                        CLASS: {
                            type: 'string',
                            defaultValue: 'greeting'
                        },
                        TEXT: {
                            type: 'string',
                            defaultValue: 'Hello'
                        }
                    }
                },
                '---',
                {
                    opcode: 'getSentiment',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'nlp.blocks.getSentiment',
                        default: 'sentiment of [TEXT]'
                    }),
                    arguments: {
                        TEXT: {
                            type: 'string',
                            defaultValue: 'I love programming'
                        }
                    }
                },
                {
                    opcode: 'getSentimentScore',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'nlp.blocks.getSentimentScore',
                        default: 'sentiment score of [TEXT]'
                    }),
                    arguments: {
                        TEXT: {
                            type: 'string',
                            defaultValue: 'I love programming'
                        }
                    }
                },
                '---',
                {
                    opcode: 'extractEntity',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'nlp.blocks.extractEntity',
                        default: 'extract [ENTITY_TYPE] from [TEXT]'
                    }),
                    arguments: {
                        ENTITY_TYPE: {
                            type: 'string',
                            menu: 'entityTypes',
                            defaultValue: 'names'
                        },
                        TEXT: {
                            type: 'string',
                            defaultValue: 'Alice and Bob went to Paris at 10am'
                        }
                    }
                },
                {
                    opcode: 'getPartOfSpeech',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'nlp.blocks.getPartOfSpeech',
                        default: 'part of speech [POS] from [TEXT]'
                    }),
                    arguments: {
                        POS: {
                            type: 'string',
                            menu: 'posTypes',
                            defaultValue: 'nouns'
                        },
                        TEXT: {
                            type: 'string',
                            defaultValue: 'The quick brown fox jumps over the lazy dog'
                        }
                    }
                }
            ],
            menus: {
                entityTypes: {
                    acceptReporters: true,
                    items: [
                        {text: 'names', value: 'names'},
                        {text: 'numbers', value: 'numbers'},
                        {text: 'places', value: 'places'},
                        {text: 'dates/times', value: 'dates/times'},
                        {text: 'urls/emails', value: 'urls/emails'}
                    ]
                },
                posTypes: {
                    acceptReporters: true,
                    items: [
                        {text: 'nouns', value: 'nouns'},
                        {text: 'verbs', value: 'verbs'},
                        {text: 'adjectives', value: 'adjectives'},
                        {text: 'adverbs', value: 'adverbs'}
                    ]
                }
            }
        };
    }

    // ═══════════════════════════════════════════════════════════
    //  Block implementations
    // ═══════════════════════════════════════════════════════════

    addDocument (args) {
        const text = args.TEXT;
        const className = args.LABEL || args.CLASS;
        this._classifier.learn(text, className);
        this._isTrained = false; // Mark it dirty, though the custom Bayes learns instantly
    }

    train () {
        return new Promise(resolve => {
            // Our naive bayes is already trained on the fly, just artificial delay
            setTimeout(() => {
                this._isTrained = true;
                resolve();
            }, 500);
        });
    }

    resetClassifier () {
        this._classifier = new NaiveBayes();
        this._isTrained = false;
    }

    analyseText (args) {
        const text = String(args.TEXT);
        if (!this._isTrained || this._classifier.totalDocs === 0) {
            return 'unknown';
        }
        return this._classifier.categorize(text);
    }

    getConfidence (args) {
        const text = String(args.TEXT);
        const className = String(args.CLASS);
        if (!this._isTrained || this._classifier.totalDocs === 0) {
            return 0;
        }
        return Math.round(Number(this._classifier.getConfidence(text, className)));
    }

    getSentiment (args) {
        const text = String(args.TEXT);
        const result = this._sentimentAnalyzer.analyze(text);
        if (result.score > 0) return 'positive';
        if (result.score < 0) return 'negative';
        return 'neutral';
    }

    getSentimentScore (args) {
        const text = String(args.TEXT);
        const result = this._sentimentAnalyzer.analyze(text);
        return result.score;
    }

    extractEntity (args) {
        const text = String(args.TEXT);
        const entityType = args.ENTITY_TYPE;
        const doc = nlp(text);
        let results = [];

        switch (entityType) {
        case 'names':
            results = doc.people().out('array');
            break;
        case 'numbers':
            results = doc.values().out('array');
            break;
        case 'places':
            results = doc.places().out('array');
            break;
        case 'dates/times':
            results = doc.match('#Date').out('array');
            break;
        case 'urls/emails':
            results = doc.urls().out('array')
                .concat(doc.emails().out('array'));
            break;
        }
        
        return results.join(', ');
    }

    getPartOfSpeech (args) {
        const text = String(args.TEXT);
        const pos = args.POS;
        const doc = nlp(text);
        let results = [];

        switch (pos) {
        case 'nouns':
            results = doc.nouns().out('array');
            break;
        case 'verbs':
            results = doc.verbs().out('array');
            break;
        case 'adjectives':
            results = doc.adjectives().out('array');
            break;
        case 'adverbs':
            results = doc.adverbs().out('array');
            break;
        }

        return results.join(', ');
    }
}

export default NLPExtension;
